// frontend/src/pages/OnboardingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import logger from '../utils/logger';

const COMPONENT_NAME = 'OnboardingPage';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated || !user) {
      logger.warn(`[${COMPONENT_NAME}] User not authenticated, redirecting to login`);
      navigate('/auth/login', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Check if user has already completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = () => {
      if (!user) {
        setIsCheckingOnboarding(false);
        return;
      }

      // Check if user has completed onboarding by verifying all required fields exist
      const hasFirstName = Boolean(user.first_name || user.firstName);
      const hasLastName = Boolean(user.last_name || user.lastName);
      const hasPhoneNumber = Boolean(user.phone_number || user.phoneNumber);

      logger.info(`[${COMPONENT_NAME}] Onboarding status check:`, {
        hasFirstName,
        hasLastName,
        hasPhoneNumber,
        user: {
          first_name: user.first_name,
          firstName: user.firstName,
          last_name: user.last_name,
          lastName: user.lastName,
          phone_number: user.phone_number,
          phoneNumber: user.phoneNumber,
        }
      });

      if (hasFirstName && hasLastName && hasPhoneNumber) {
        logger.info(`[${COMPONENT_NAME}] User has already completed onboarding, redirecting to home`);
        navigate('/', { replace: true });
        return;
      }

      setIsCheckingOnboarding(false);
    };

    checkOnboardingStatus();
  }, [user, navigate]);

  // Pre-fill form with existing user data
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        phoneNumber: user.phone_number || user.phoneNumber || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }
    
    // Basic phone validation
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !user.id) {
      logger.error(`[${COMPONENT_NAME}] User object is null or missing ID`);
      setError('Authentication error. Please log in again.');
      setTimeout(() => navigate('/auth/login', { replace: true }), 2000);
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      logger.info(`[${COMPONENT_NAME}] Updating user profile for user ID: ${user.id}`);

      const updateData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone_number: formData.phoneNumber.trim(),
      };

      const token = localStorage.getItem('token');
      const baseURL = 'https://z-poel.onrender.com/api/v1';

      logger.info(`[${COMPONENT_NAME}] Sending update request with data:`, updateData);
      console.log('🚀 SENDING UPDATE REQUEST:', updateData);
      console.log('🔗 API BASE URL:', baseURL);

      // ✅ FIX: Try different endpoints to find the correct one
      let response;
      let updatedUserData = null;

      // Try PUT /users/profile first
      try {
        response = await axios.put(
          `${baseURL}/users/profile`,
          updateData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        updatedUserData = response.data.user || response.data.data?.user || response.data;
        console.log('✅ UPDATE RESPONSE (PUT /users/profile):', response.data);
      } catch (putError) {
        // If PUT /users/profile fails, try PATCH /users/me
        if (putError.response?.status === 404) {
          console.log('⚠️ PUT /users/profile not found, trying PATCH /users/me...');
          response = await axios.patch(
            `${baseURL}/users/me`,
            updateData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          updatedUserData = response.data.user || response.data.data?.user || response.data;
          console.log('✅ UPDATE RESPONSE (PATCH /users/me):', response.data);
        } else {
          throw putError;
        }
      }

      logger.info(`[${COMPONENT_NAME}] Profile updated successfully:`, response.data);

      // ✅ FIX: Update user context with the returned data from update endpoint
      // No need to make another API call that might fail
      if (updatedUserData) {
        // Merge the update data with existing user data
        const mergedUserData = {
          ...user,
          ...updatedUserData,
          first_name: formData.firstName.trim(),
          firstName: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          lastName: formData.lastName.trim(),
          phone_number: formData.phoneNumber.trim(),
          phoneNumber: formData.phoneNumber.trim(),
        };
        
        setUser(mergedUserData);
        console.log('✅ UPDATED USER IN CONTEXT:', mergedUserData);
        logger.info(`[${COMPONENT_NAME}] User context updated with profile data`);
      }
      
      logger.info(`[${COMPONENT_NAME}] Profile update complete, redirecting to home`);
      
      // Redirect to home page
      navigate('/', { replace: true });

    } catch (err) {
      console.error('❌ UPDATE ERROR:', err.response || err);
      logger.error(`[${COMPONENT_NAME}] Error updating profile:`, err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update profile. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking onboarding status
  if (isCheckingOnboarding || !user) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <img
            src="/connectsphere_logo_transparent.png"
            alt="ConnectSphere Logo"
            className="onboarding-logo"
          />
          <h1 className="onboarding-title">Complete Your Profile</h1>
          <p className="onboarding-subtitle">
            We need a bit more information to get you started
          </p>
        </div>

        {error && (
          <div className="onboarding-error" role="alert">
            <svg className="error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={isLoading}
              required
              className="form-input"
              placeholder="John"
              autoComplete="given-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={isLoading}
              required
              className="form-input"
              placeholder="Doe"
              autoComplete="family-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={isLoading}
              required
              className="form-input"
              placeholder="+1 (555) 123-4567"
              autoComplete="tel"
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary btn-full"
            >
              {isLoading ? (
                <>
                  <span className="button-spinner"></span>
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>

        <p className="onboarding-footer">
          You can always update this information later in your profile settings
        </p>
      </div>

      <style jsx>{`
        .onboarding-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 1rem;
        }

        .onboarding-card {
          width: 100%;
          max-width: 480px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          padding: 2.5rem;
          animation: slideUp 0.4s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .onboarding-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .onboarding-logo {
          height: 64px;
          width: auto;
          margin-bottom: 1.5rem;
        }

        .onboarding-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 0.5rem;
        }

        .onboarding-subtitle {
          font-size: 0.95rem;
          color: #718096;
        }

        .onboarding-error {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #fee;
          border: 1px solid #fcc;
          border-radius: 8px;
          color: #c53030;
          margin-bottom: 1.5rem;
          animation: shake 0.4s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        .error-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .onboarding-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2d3748;
        }

        .required {
          color: #e53e3e;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 1rem;
          color: #2d3748;
          background: white;
          transition: all 0.2s;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-input:disabled {
          background: #f7fafc;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .form-input::placeholder {
          color: #a0aec0;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }

        .btn-primary,
        .btn-secondary {
          flex: 1;
          padding: 0.875rem 1.5rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-full {
          width: 100%;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }

        .btn-primary:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: white;
          color: #4a5568;
          border: 2px solid #e2e8f0;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f7fafc;
          border-color: #cbd5e0;
        }

        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .onboarding-footer {
          text-align: center;
          font-size: 0.8rem;
          color: #a0aec0;
          margin-top: 1.5rem;
        }

        @media (max-width: 640px) {
          .onboarding-card {
            padding: 2rem 1.5rem;
          }

          .onboarding-title {
            font-size: 1.5rem;
          }

          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingPage;
